import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { maskPhone } from "@/lib/phone";
import { toast } from "sonner";
import {
  formatDateTime,
  smartDueLabel,
  smartDueUrgency,
  groupFollowUpsByUrgency,
  FOLLOWUP_PRESETS,
  CHANNEL_OPTIONS,
  LOST_STATUSES,
} from "@/lib/constants";
import { useMasterData } from "@/hooks/use-master-data";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  CheckCircle2,
  Phone,
  MessageCircle,
  Mail,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Plus,
  CalendarClock,
  Check,
  ChevronsUpDown,
  Flame,
  Bell,
  ArrowRight,
  Activity,
  TrendingUp,
} from "lucide-react";

import ChannelSelector from "@/components/crm/followups/ChannelSelector";
import DueDatePresets from "@/components/crm/followups/DueDatePresets";
import OutcomeChipGrid from "@/components/crm/followups/OutcomeChipGrid";
import AttemptDots from "@/components/crm/followups/AttemptDots";
import FollowUpEmptyState from "@/components/crm/followups/FollowUpEmptyState";
import SmartDueLabel from "@/components/crm/followups/SmartDueLabel";

// ── Helpers ──────────────────────────────────────────────────────────────────

const CHANNEL_ICON = {
  call: Phone,
  whatsapp: MessageCircle,
  email: Mail,
  in_app: Bell,
};

const PRIORITY_STRIPE = {
  urgent: "border-l-red-500",
  high: "border-l-orange-400",
  normal: "border-l-slate-300",
  low: "border-l-gray-200",
};

const labelOf = (opts, val) =>
  opts.find((o) => o.value === val)?.label || val;
const colorOf = (opts, val) =>
  opts.find((o) => o.value === val)?.color || "bg-gray-100 text-gray-700";

function Badge({ children, className = "" }) {
  return (
    <span
      className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${className}`}
    >
      {children}
    </span>
  );
}

// ── NurtureDashboard ─────────────────────────────────────────────────────────

function AttentionBar({ items }) {
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);

  const urgentCount = items.filter(
    (f) => f.priority === "urgent" && f.status !== "done"
  ).length;
  const overdueCount = items.filter((f) => f.status === "overdue").length;
  const criticalCount = urgentCount + overdueCount;

  const todayCount = items.filter(
    (f) => f.due_at?.slice(0, 10) === todayStr && f.status !== "done"
  ).length;

  const weekFromNow = new Date(now.getTime() + 7 * 86400000)
    .toISOString()
    .slice(0, 10);
  const thisWeekCount = items.filter(
    (f) =>
      f.status !== "done" &&
      f.due_at?.slice(0, 10) >= todayStr &&
      f.due_at?.slice(0, 10) <= weekFromNow
  ).length;

  const completedTodayCount = items.filter(
    (f) => f.status === "done" && f.due_at?.slice(0, 10) === todayStr
  ).length;

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
      {/* Critical zone */}
      <div className="flex flex-1 items-center gap-4 rounded-xl border border-red-200 bg-red-50/60 px-5 py-4 shadow-sm">
        <div className="relative">
          <AlertCircle className="h-8 w-8 text-red-500" />
          {criticalCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" />
          )}
        </div>
        <div>
          <span className="text-3xl font-bold font-display leading-none text-red-700">
            {criticalCount}
          </span>
          <p className="mt-0.5 text-xs font-medium uppercase tracking-wider text-red-600/70">
            Need Attention
          </p>
          <p className="text-[10px] text-red-500/60">
            {urgentCount} urgent + {overdueCount} overdue
          </p>
        </div>
      </div>

      {/* Metric pills */}
      <div className="flex flex-1 gap-3">
        <div className="flex flex-1 items-center gap-3 rounded-xl border border-amber-200 bg-white px-4 py-3 shadow-sm">
          <Clock className="h-5 w-5 text-amber-500" />
          <div>
            <span className="text-xl font-bold font-display leading-none text-amber-700">
              {todayCount}
            </span>
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Today
            </p>
          </div>
        </div>
        <div className="flex flex-1 items-center gap-3 rounded-xl border border-blue-200 bg-white px-4 py-3 shadow-sm">
          <CalendarClock className="h-5 w-5 text-blue-500" />
          <div>
            <span className="text-xl font-bold font-display leading-none text-blue-700">
              {thisWeekCount}
            </span>
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              This Week
            </p>
          </div>
        </div>
        <div className="flex flex-1 items-center gap-3 rounded-xl border border-emerald-200 bg-white px-4 py-3 shadow-sm">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <div>
            <span className="text-xl font-bold font-display leading-none text-emerald-700">
              {completedTodayCount}
            </span>
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Done Today
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConversionFunnel({ items, md }) {
  const completedItems = items.filter((f) => f.status === "done" && f.outcome);
  const total = completedItems.length;

  const outcomeCounts = useMemo(() => {
    const counts = {};
    for (const f of completedItems) {
      counts[f.outcome] = (counts[f.outcome] || 0) + 1;
    }
    return md.followupOutcomes
      .map((o) => ({
        ...o,
        count: counts[o.value] || 0,
      }))
      .filter((o) => o.count > 0);
  }, [completedItems, md.followupOutcomes]);

  if (total === 0) return null;

  return (
    <div className="rounded-xl border border-navy/10 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-navy/50" />
        <span className="text-xs font-semibold uppercase tracking-wider text-navy/50">
          Outcome Breakdown
        </span>
        <span className="ml-auto text-xs text-muted-foreground">
          {total} completed
        </span>
      </div>
      {/* Bar */}
      <div className="flex h-3 overflow-hidden rounded-full bg-navy/5">
        {outcomeCounts.map((o) => (
          <div
            key={o.value}
            className={`transition-all ${o.color || "bg-gray-300"}`}
            style={{ width: `${(o.count / total) * 100}%` }}
            title={`${o.label}: ${o.count}`}
          />
        ))}
      </div>
      {/* Legend */}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {outcomeCounts.map((o) => (
          <span key={o.value} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span
              className={`inline-block h-2 w-2 rounded-full ${o.color || "bg-gray-300"}`}
            />
            {o.label}
            <span className="font-semibold text-ink">{o.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function NurtureDashboard({ items, md }) {
  return (
    <div className="space-y-3">
      <AttentionBar items={items} />
      <ConversionFunnel items={items} md={md} />
    </div>
  );
}

// ── Section Header ───────────────────────────────────────────────────────────

const SECTION_STYLES = {
  immediate: {
    bar: "bg-red-500",
    text: "text-red-700",
    label: "Needs Attention",
    Icon: Flame,
  },
  today: {
    bar: "bg-amber-400",
    text: "text-amber-700",
    label: "Today",
    Icon: Clock,
  },
  upcoming: {
    bar: "bg-blue-400",
    text: "text-blue-700",
    label: "Upcoming",
    Icon: CalendarClock,
  },
  completed: {
    bar: "bg-emerald-400",
    text: "text-emerald-700",
    label: "Completed",
    Icon: CheckCircle2,
  },
};

function SectionHeader({ section, count }) {
  const s = SECTION_STYLES[section] || SECTION_STYLES.upcoming;
  const Icon = s.Icon;
  return (
    <div className="flex items-center gap-2 py-2">
      <div className={`h-5 w-1 rounded-full ${s.bar}`} />
      <Icon className={`h-4 w-4 ${s.text}`} />
      <span className={`text-sm font-semibold ${s.text}`}>{s.label}</span>
      <span className="rounded-full bg-navy/5 px-2 py-0.5 text-[10px] font-bold text-navy">
        {count}
      </span>
    </div>
  );
}

// ── NurtureCard ──────────────────────────────────────────────────────────────

function NurtureCard({ f, md, onComplete, onReschedule, onNavigate }) {
  const [expanded, setExpanded] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const Icon = CHANNEL_ICON[f.channel] || Clock;
  const overdue = f.status === "overdue";
  const done = f.status === "done";
  const stripe = PRIORITY_STRIPE[f.priority] || PRIORITY_STRIPE.normal;

  return (
    <>
      <div
        className={`rounded-xl border border-l-4 shadow-sm transition-all ${stripe} ${
          overdue
            ? "border-red-300 bg-red-50/40"
            : "border-navy/10 bg-white"
        }`}
        data-testid={`followup-${f.id}`}
      >
        <div className="flex items-start justify-between gap-3 p-4">
          {/* Left content */}
          <div className="flex items-start gap-3 min-w-0 flex-1">
            {/* Channel icon circle */}
            <div
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                overdue
                  ? "bg-red-100 text-red-600"
                  : done
                  ? "bg-emerald-100 text-emerald-600"
                  : "bg-navy/10 text-navy"
              }`}
            >
              {done ? (
                <CheckCircle2 className="h-3.5 w-3.5" />
              ) : overdue ? (
                <AlertCircle className="h-3.5 w-3.5" />
              ) : (
                <Icon className="h-3.5 w-3.5" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              {/* Title row with attempt dots */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <p
                  className={`font-medium text-sm truncate ${
                    done ? "text-muted-foreground line-through" : "text-ink"
                  }`}
                >
                  {f.title}
                </p>
                {f.priority === "urgent" && (
                  <Flame className="h-3.5 w-3.5 text-red-500 shrink-0" />
                )}
                <AttemptDots count={f.attempt_no} />
              </div>

              {/* Lead context row */}
              {f.lead_name && (
                <div className="mt-1 flex items-center gap-1.5">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-navy/10 text-[10px] font-bold text-navy">
                    {f.lead_name.charAt(0).toUpperCase()}
                  </span>
                  <button
                    onClick={() => f.lead_id && onNavigate(`/leads/${f.lead_id}`)}
                    className="text-xs text-navy hover:underline truncate"
                  >
                    {f.lead_name}
                  </button>
                </div>
              )}

              {/* Badge row */}
              <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                {f.followup_type && (
                  <Badge className={colorOf(md.followupTypes, f.followup_type)}>
                    {labelOf(md.followupTypes, f.followup_type)}
                  </Badge>
                )}
                {f.priority && f.priority !== "normal" && (
                  <Badge
                    className={colorOf(md.followupPriorities, f.priority)}
                  >
                    {labelOf(md.followupPriorities, f.priority)}
                  </Badge>
                )}
                {done && f.outcome && (
                  <Badge className={colorOf(md.followupOutcomes, f.outcome)}>
                    {labelOf(md.followupOutcomes, f.outcome)}
                  </Badge>
                )}
                <span className="text-[10px] text-muted-foreground capitalize">
                  {f.channel}
                </span>
              </div>

              {/* Smart due label */}
              <div className="mt-1.5">
                <SmartDueLabel dueAt={f.due_at} />
              </div>

              {/* Expandable notes */}
              {(f.notes || f.outcome_note) && (
                <>
                  <button
                    onClick={() => setExpanded((x) => !x)}
                    className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground hover:text-ink"
                  >
                    {expanded ? (
                      <ChevronUp className="h-3 w-3" />
                    ) : (
                      <ChevronDown className="h-3 w-3" />
                    )}
                    {expanded ? "Hide details" : "Show details"}
                  </button>
                  {expanded && (
                    <div className="mt-2 space-y-2 rounded-lg border border-navy/5 bg-nude/30 p-2.5">
                      {f.notes && (
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">
                            Note
                          </p>
                          <p className="text-xs text-ink whitespace-pre-wrap">
                            {f.notes}
                          </p>
                        </div>
                      )}
                      {f.outcome_note && (
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">
                            Outcome note
                          </p>
                          <p className="text-xs text-ink whitespace-pre-wrap">
                            {f.outcome_note}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Action buttons */}
          {!done && (
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={() => setRescheduleOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 transition-colors"
                data-testid={`reschedule-followup-${f.id}`}
              >
                <CalendarClock className="h-3.5 w-3.5" />
                Reschedule
              </button>
              <button
                onClick={() => onComplete(f)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
                data-testid={`done-followup-${f.id}`}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Complete
              </button>
            </div>
          )}
        </div>
      </div>

      <RescheduleDialog
        open={rescheduleOpen}
        onOpenChange={setRescheduleOpen}
        followUp={f}
        onDone={onReschedule}
      />
    </>
  );
}

// ── Complete Dialog ──────────────────────────────────────────────────────────

function CompleteDialog({ open, onOpenChange, followUp, md, onDone }) {
  const [outcome, setOutcome] = useState("");
  const [outcomeNote, setOutcomeNote] = useState("");
  const [scheduleNext, setScheduleNext] = useState(false);
  const [nextDue, setNextDue] = useState("");
  const [nextType, setNextType] = useState("");
  const [nextPriority, setNextPriority] = useState("normal");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setOutcome("");
      setOutcomeNote("");
      setScheduleNext(false);
      setNextDue("");
      setNextType(followUp?.followup_type || "general");
      setNextPriority(followUp?.priority || "normal");
    }
  }, [open, followUp]);

  const submit = async () => {
    if (!outcome) {
      toast.error("Pick an outcome");
      return;
    }
    if (scheduleNext && !nextDue) {
      toast.error("Pick the next due date");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/followups/${followUp.id}/complete`, {
        outcome,
        outcome_note: outcomeNote,
        next_due_at: scheduleNext ? new Date(nextDue).toISOString() : null,
        next_type: scheduleNext ? nextType : null,
        next_priority: scheduleNext ? nextPriority : null,
      });
      toast.success(
        scheduleNext
          ? "Completed -- next follow-up scheduled"
          : "Follow-up completed"
      );
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(
        formatApiError(e.response?.data?.detail) || "Failed to complete"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">
            Complete Follow-up
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Attempt chain context */}
          {followUp && (followUp.attempt_no || 1) > 1 && (
            <div className="flex items-center gap-2 rounded-lg border border-navy/10 bg-cream/40 px-3 py-2">
              <Activity className="h-4 w-4 text-navy/50" />
              <span className="text-xs text-navy/70">
                This is attempt{" "}
                <span className="font-bold text-navy">
                  #{followUp.attempt_no}
                </span>{" "}
                in the follow-up chain
              </span>
              <AttemptDots count={followUp.attempt_no} />
            </div>
          )}

          <p className="text-sm text-muted-foreground truncate">
            {followUp?.title}
          </p>

          {/* Outcome chip grid (replaces Select) */}
          <div className="space-y-1.5">
            <Label>Outcome *</Label>
            <OutcomeChipGrid
              outcomes={md.followupOutcomes}
              value={outcome}
              onChange={setOutcome}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Outcome note</Label>
            <Textarea
              value={outcomeNote}
              onChange={(e) => setOutcomeNote(e.target.value)}
              rows={2}
              placeholder="e.g. Patient asked to call after 6pm"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer pt-1">
            <Checkbox
              checked={scheduleNext}
              onCheckedChange={(v) => setScheduleNext(!!v)}
            />
            <span className="text-sm font-medium text-ink">
              Schedule next follow-up
            </span>
          </label>

          {scheduleNext && (
            <div className="space-y-3 rounded-lg border border-navy/10 bg-cream/40 p-3">
              <div className="space-y-1.5">
                <Label>Next due date & time *</Label>
                <Input
                  type="datetime-local"
                  value={nextDue}
                  onChange={(e) => setNextDue(e.target.value)}
                  data-testid="fu-next-due"
                />
                <DueDatePresets onSelect={setNextDue} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Type</Label>
                  <Select value={nextType} onValueChange={setNextType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {md.followupTypes.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Priority</Label>
                  <Select value={nextPriority} onValueChange={setNextPriority}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {md.followupPriorities.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="fu-complete-submit"
          >
            {saving ? "Saving..." : "Complete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Reschedule Dialog ────────────────────────────────────────────────────────

function RescheduleDialog({ open, onOpenChange, followUp, onDone }) {
  const [due_at, setDueAt] = useState("");

  useEffect(() => {
    if (open && followUp?.due_at) {
      const local = new Date(followUp.due_at);
      local.setMinutes(local.getMinutes() - local.getTimezoneOffset());
      setDueAt(local.toISOString().slice(0, 16));
    }
  }, [open, followUp]);

  const submit = async () => {
    if (!due_at) {
      toast.error("Please pick a new date and time");
      return;
    }
    try {
      await api.put(`/followups/${followUp.id}`, {
        due_at: new Date(due_at).toISOString(),
        status: "pending",
      });
      toast.success("Follow-up rescheduled");
      onOpenChange(false);
      onDone();
    } catch {
      toast.error("Failed to reschedule");
    }
  };

  const oldDueFormatted = followUp?.due_at
    ? formatDateTime(followUp.due_at)
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">
            Reschedule Follow-up
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground truncate">
            {followUp?.title}
          </p>

          {/* Old date crossed out */}
          {oldDueFormatted && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">Current:</span>
              <span className="line-through text-red-400">
                {oldDueFormatted}
              </span>
              <ArrowRight className="h-3 w-3 text-muted-foreground" />
              <span className="font-medium text-navy">New date below</span>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>New date & time</Label>
            <Input
              type="datetime-local"
              value={due_at}
              onChange={(e) => setDueAt(e.target.value)}
            />
            <DueDatePresets onSelect={setDueAt} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            className="bg-navy text-white hover:bg-navy-hover"
          >
            Reschedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Create Follow-up Dialog ──────────────────────────────────────────────────

function CreateFollowUpDialog({ open, onOpenChange, md, onDone }) {
  const empty = {
    title: "",
    lead_id: "",
    channel: "call",
    followup_type: "call_back",
    priority: "normal",
    due_at: "",
    notes: "",
  };
  const [form, setForm] = useState(empty);
  const [leads, setLeads] = useState([]);
  const [leadPopoverOpen, setLeadPopoverOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(empty);
      api
        .get("/leads", { params: { limit: 200 } })
        // Lost leads have their follow-ups auto-closed and POST /followups is
        // rejected for them — keep them out of the lead picker.
        .then((r) =>
          setLeads(
            (r.data?.items || r.data || []).filter(
              (l) => !LOST_STATUSES.includes((l.status || "").toLowerCase())
            )
          )
        )
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedLead = leads.find((l) => l.id === form.lead_id);
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const applyPreset = (preset) => {
    const due = new Date(Date.now() + preset.offsetHours * 3600000);
    set("title", preset.title);
    set("followup_type", preset.type);
    set("channel", preset.channel);
    set("priority", preset.priority);
    set("due_at", due.toISOString().slice(0, 16));
  };

  const submit = async () => {
    if (!form.title || !form.due_at) {
      toast.error("Title and due date are required");
      return;
    }
    try {
      await api.post("/followups", {
        ...form,
        lead_id:
          form.lead_id && form.lead_id !== "none" ? form.lead_id : null,
        due_at: new Date(form.due_at).toISOString(),
      });
      toast.success("Follow-up scheduled");
      setForm(empty);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(
        formatApiError(e.response?.data?.detail) ||
          "Failed to create follow-up"
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">
            Schedule Follow-up
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Quick preset chips */}
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Quick presets
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {FOLLOWUP_PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className="rounded-full border border-navy/10 bg-navy/5 px-3 py-1 text-xs font-medium text-navy hover:bg-navy/10 transition-colors"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. Call back after consultation"
              data-testid="fu-title"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Lead (optional)</Label>
            <Popover open={leadPopoverOpen} onOpenChange={setLeadPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between font-normal text-left h-9"
                >
                  <span className="truncate text-sm">
                    {selectedLead
                      ? `${selectedLead.name}${
                          selectedLead.mobile
                            ? ` · ${maskPhone(selectedLead.mobile)}`
                            : ""
                        }`
                      : "Select lead..."}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[380px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search by name or mobile..." />
                  <CommandList>
                    <CommandEmpty>No leads found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="none"
                        onSelect={() => {
                          set("lead_id", "");
                          setLeadPopoverOpen(false);
                        }}
                      >
                        <Check
                          className={`mr-2 h-4 w-4 ${
                            !form.lead_id ? "opacity-100" : "opacity-0"
                          }`}
                        />
                        No lead
                      </CommandItem>
                      {leads.map((l) => (
                        <CommandItem
                          key={l.id}
                          value={`${l.name} ${l.mobile || ""}`}
                          onSelect={() => {
                            set("lead_id", l.id);
                            setLeadPopoverOpen(false);
                          }}
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${
                              form.lead_id === l.id
                                ? "opacity-100"
                                : "opacity-0"
                            }`}
                          />
                          <span>{l.name}</span>
                          {l.mobile && (
                            <span className="ml-1 text-xs text-muted-foreground">
                              {"·"} {maskPhone(l.mobile)}
                            </span>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Channel selector (replaces dropdown) */}
          <div className="space-y-1.5">
            <Label>Channel</Label>
            <ChannelSelector
              value={form.channel}
              onChange={(v) => set("channel", v)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select
                value={form.followup_type}
                onValueChange={(v) => set("followup_type", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {md.followupTypes.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select
                value={form.priority}
                onValueChange={(v) => set("priority", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {md.followupPriorities.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Due date with presets */}
          <div className="space-y-1.5">
            <Label>Due date & time *</Label>
            <Input
              type="datetime-local"
              value={form.due_at}
              onChange={(e) => set("due_at", e.target.value)}
              data-testid="fu-due"
            />
            <DueDatePresets onSelect={(v) => set("due_at", v)} />
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Any context for this follow-up..."
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            className="bg-navy text-white hover:bg-navy-hover"
            data-testid="fu-submit"
          >
            Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function FollowUps() {
  const navigate = useNavigate();
  const md = useMasterData();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterAssignee, setFilterAssignee] = useState("all");
  const [filterChannel, setFilterChannel] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [completeFu, setCompleteFu] = useState(null);

  const load = useCallback(() => {
    api
      .get("/followups")
      .then((r) => setItems(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api
      .get("/users")
      .then((r) => setUsers(r.data))
      .catch(() => {});
  }, [load]);

  // ── Filtering ────────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return items.filter((f) => {
      // Tab filters
      if (tab === "action") {
        if (f.status === "done") return false;
        if (f.status !== "overdue" && f.priority !== "urgent" && f.due_at?.slice(0, 10) !== today)
          return false;
      }
      if (tab === "scheduled" && f.status === "done") return false;
      if (tab === "scheduled" && f.status === "overdue") return false;
      if (tab === "completed" && f.status !== "done") return false;

      // Field filters
      if (filterType !== "all" && f.followup_type !== filterType) return false;
      if (filterPriority !== "all" && f.priority !== filterPriority)
        return false;
      if (filterAssignee !== "all" && f.assigned_to !== filterAssignee)
        return false;
      if (filterChannel !== "all" && f.channel !== filterChannel) return false;

      return true;
    });
  }, [items, tab, filterType, filterPriority, filterAssignee, filterChannel]);

  // ── Grouped stream ─────────────────────────────────────────────────────

  const groups = useMemo(
    () => groupFollowUpsByUrgency(filtered),
    [filtered]
  );

  const hasAnyItems =
    groups.immediate.length > 0 ||
    groups.today.length > 0 ||
    groups.upcoming.length > 0 ||
    groups.completed.length > 0;

  const hasFilters =
    filterType !== "all" ||
    filterPriority !== "all" ||
    filterAssignee !== "all" ||
    filterChannel !== "all";

  // ── Tab label helper ───────────────────────────────────────────────────

  const tabLabel = useMemo(() => {
    if (tab === "action") return "action";
    if (tab === "scheduled") return "scheduled";
    if (tab === "completed") return "completed";
    return "all";
  }, [tab]);

  return (
    <div className="space-y-5">
      {/* Nurture Dashboard */}
      <NurtureDashboard items={items} md={md} />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white">
            <TabsTrigger value="all" data-testid="followup-tab-all">
              All
            </TabsTrigger>
            <TabsTrigger value="action" data-testid="followup-tab-action">
              Action Required
            </TabsTrigger>
            <TabsTrigger value="scheduled" data-testid="followup-tab-scheduled">
              Scheduled
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="followup-tab-completed">
              Completed
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger
              className="h-9 w-36 bg-white"
              data-testid="fu-filter-type"
            >
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {md.followupTypes.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger
              className="h-9 w-36 bg-white"
              data-testid="fu-filter-priority"
            >
              <SelectValue placeholder="All Priorities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              {md.followupPriorities.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterAssignee} onValueChange={setFilterAssignee}>
            <SelectTrigger
              className="h-9 w-36 bg-white"
              data-testid="fu-filter-assignee"
            >
              <SelectValue placeholder="All Assignees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assignees</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterChannel} onValueChange={setFilterChannel}>
            <SelectTrigger
              className="h-9 w-36 bg-white"
              data-testid="fu-filter-channel"
            >
              <SelectValue placeholder="All Channels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Channels</SelectItem>
              {CHANNEL_OPTIONS.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasFilters && (
            <Button
              variant="ghost"
              onClick={() => {
                setFilterType("all");
                setFilterPriority("all");
                setFilterAssignee("all");
                setFilterChannel("all");
              }}
              className="h-9 px-3 text-xs text-muted-foreground hover:text-ink"
              data-testid="fu-filter-clear"
            >
              Clear filters
            </Button>
          )}

          <Button
            className="bg-navy text-white hover:bg-navy-hover"
            data-testid="new-followup-btn"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Schedule Follow-up
          </Button>
        </div>
      </div>

      {/* Grouped Stream */}
      {!hasAnyItems ? (
        <FollowUpEmptyState
          tab={tabLabel}
          onSchedule={() => setCreateOpen(true)}
        />
      ) : (
        <div className="space-y-2">
          {groups.immediate.length > 0 && (
            <div>
              <SectionHeader
                section="immediate"
                count={groups.immediate.length}
              />
              <div className="space-y-2">
                {groups.immediate.map((f) => (
                  <NurtureCard
                    key={f.id}
                    f={f}
                    md={md}
                    onComplete={setCompleteFu}
                    onReschedule={load}
                    onNavigate={navigate}
                  />
                ))}
              </div>
            </div>
          )}

          {groups.today.length > 0 && (
            <div>
              <SectionHeader section="today" count={groups.today.length} />
              <div className="space-y-2">
                {groups.today.map((f) => (
                  <NurtureCard
                    key={f.id}
                    f={f}
                    md={md}
                    onComplete={setCompleteFu}
                    onReschedule={load}
                    onNavigate={navigate}
                  />
                ))}
              </div>
            </div>
          )}

          {groups.upcoming.length > 0 && (
            <div>
              <SectionHeader
                section="upcoming"
                count={groups.upcoming.length}
              />
              <div className="space-y-2">
                {groups.upcoming.map((f) => (
                  <NurtureCard
                    key={f.id}
                    f={f}
                    md={md}
                    onComplete={setCompleteFu}
                    onReschedule={load}
                    onNavigate={navigate}
                  />
                ))}
              </div>
            </div>
          )}

          {groups.completed.length > 0 && (
            <div>
              <SectionHeader
                section="completed"
                count={groups.completed.length}
              />
              <div className="space-y-2">
                {groups.completed.map((f) => (
                  <NurtureCard
                    key={f.id}
                    f={f}
                    md={md}
                    onComplete={setCompleteFu}
                    onReschedule={load}
                    onNavigate={navigate}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dialogs */}
      <CreateFollowUpDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        md={md}
        onDone={load}
      />
      <CompleteDialog
        open={!!completeFu}
        onOpenChange={(o) => {
          if (!o) setCompleteFu(null);
        }}
        followUp={completeFu}
        md={md}
        onDone={load}
      />
    </div>
  );
}
